import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
  signal
} from '@angular/core'
import { animate, style, transition, trigger } from '@angular/animations'
import { PA_UI_COPY } from '../pa-ui.copy'
import { PaUnit } from '../pa-ui.types'
import { PREFERS_REDUCED_MOTION } from '../pa-reduced-motion'

/**
 * "All units" / "Specific units" choice, with a checkbox grid for the specific case. Presentational:
 * `units` and `selected` in, `selectedChange` out; an empty selection means org-wide.
 */
@Component({
  selector: 'aiomed-pa-unit-multiselect',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('listExpand', [
      transition(':enter', [
        style({ opacity: 0, height: 0, transform: 'translateY(-4px)' }),
        animate(
          '180ms ease-out',
          style({ opacity: 1, height: '*', transform: 'none' })
        )
      ]),
      transition(':leave', [
        animate(
          '140ms ease-in',
          style({ opacity: 0, height: 0, transform: 'translateY(-4px)' })
        )
      ])
    ])
  ],
  templateUrl: './pa-unit-multiselect.component.html',
  styleUrls: ['./pa-unit-multiselect.component.scss']
})
export class PaUnitMultiselectComponent {
  readonly units = input.required<PaUnit[]>()
  readonly selected = input.required<string[]>()
  /** Org name for the "All units in {orgName}" label; empty gives a bare "All units". */
  readonly orgName = input<string>('')
  readonly selectedChange = output<string[]>()

  protected readonly copy = PA_UI_COPY.unitMultiselect
  // Reduced motion disables the units-list expand animation.
  protected readonly reduceMotion = PREFERS_REDUCED_MOTION

  protected readonly allUnitsLabel = computed(() => {
    const name = this.orgName().trim()
    return name ? this.copy.selectAllIn(name) : this.copy.selectAll
  })

  // Lets "Specific units" show the grid before any box is checked; an empty selection alone means All.
  private readonly specificSignal = signal(false)

  protected readonly mode = computed<'all' | 'specific'>(() =>
    this.specificSignal() || this.selected().length > 0 ? 'specific' : 'all'
  )

  protected isChecked(id: string): boolean {
    return this.selected().includes(id)
  }

  protected pickAll(): void {
    this.specificSignal.set(false)
    if (this.selected().length > 0) {
      this.selectedChange.emit([])
    }
  }

  protected pickSpecific(): void {
    this.specificSignal.set(true)
  }

  protected toggleUnit(id: string): void {
    const current = this.selected()
    const next = current.includes(id)
      ? current.filter(u => u !== id)
      : [...current, id]
    this.selectedChange.emit(next)
  }
}
